/**
 * Hybrid Engine - Combines Minimax, MCTS, and Pattern Recognition
 * "Zenith" - The ultimate AI combining all approaches
 */

import { GameState, IMove, Position } from "../../types";
import { ZodiacEngine } from "../../zodiacEngine";
import { MinimaxEngine } from "../MinimaxAI/MinimaxEngine";
import { MCTSEngine } from "../MonteCarloAI/MCTSEngine";
import { PatternLibrary } from "./PatternLibrary";
import { PositionEvaluator } from "../MinimaxAI/PositionEvaluator";

export interface HybridResult {
  fromPosition: Position;
  move: IMove;
  score: number;
  method: "pattern" | "tactical_minimax" | "mcts" | "hybrid";
  confidence: number;
  details: string;
}

export class HybridEngine {
  private minimaxEngine: MinimaxEngine;
  private mctsEngine: MCTSEngine;
  private patternLibrary: PatternLibrary;
  private gameEngine: ZodiacEngine;

  constructor(gameEngine: ZodiacEngine) {
    this.gameEngine = gameEngine;
    this.minimaxEngine = new MinimaxEngine(gameEngine);
    this.mctsEngine = new MCTSEngine();
    this.patternLibrary = new PatternLibrary();
  }

  /**
   * Find best move using hybrid approach
   * Intelligently allocates time between different methods
   */
  findBestMove(gameState: GameState, maxTime: number): HybridResult | null {
    const startTime = Date.now();

    // Phase 1: Quick pattern check (100ms max)
    const patternMove = this.patternLibrary.getSuggestedMove(gameState);
    if (patternMove && Date.now() - startTime < 100) {
      const patternStrength = this.evaluatePatternStrength(gameState);
      if (patternStrength > 0.8) {
        return {
          fromPosition: patternMove.fromPosition,
          move: patternMove.move,
          score: 0,
          method: "pattern",
          confidence: patternStrength,
          details: "High-confidence pattern recognition",
        };
      }
    }

    // Phase 2: Check for RACE EMERGENCY (highest priority!)
    const raceStatus = this.checkRaceEmergency(gameState);

    // Phase 3: Determine position type
    const positionType = this.classifyPosition(gameState);

    // Phase 4: Allocate time based on position type and race status
    let minimaxTime = 0;
    let mctsTime = 0;

    if (raceStatus === "critical") {
      // RACE EMERGENCY! Give almost all time to minimax (it can now detect race threats!)
      minimaxTime = maxTime * 0.95;
      mctsTime = maxTime * 0.05;
    } else if (raceStatus === "dangerous") {
      // Race getting close - favor minimax
      minimaxTime = maxTime * 0.80;
      mctsTime = maxTime * 0.20;
    } else {
      // Normal time allocation based on position type
      switch (positionType) {
        case "tactical":
          // Tactical positions: 70% minimax, 30% MCTS
          minimaxTime = maxTime * 0.7;
          mctsTime = maxTime * 0.3;
          break;
        case "complex":
          // Complex positions: 30% minimax, 70% MCTS
          minimaxTime = maxTime * 0.3;
          mctsTime = maxTime * 0.7;
          break;
        case "endgame":
          // Endgame: 85% minimax, 15% MCTS (can search deeper!)
          minimaxTime = maxTime * 0.85;
          mctsTime = maxTime * 0.15;
          break;
        default:
          // Balanced: 50% minimax, 50% MCTS
          minimaxTime = maxTime * 0.5;
          mctsTime = maxTime * 0.5;
      }
    }

    // Phase 4: Run minimax analysis
    let minimaxMove: any = null;
    let minimaxScore = -Infinity;

    if (minimaxTime > 500) {
      const remainingTime = maxTime - (Date.now() - startTime);
      const actualMinimaxTime = Math.min(minimaxTime, remainingTime * 0.95);

      minimaxMove = this.minimaxEngine.findBestMove(gameState, actualMinimaxTime);

      if (minimaxMove) {
        minimaxScore = minimaxMove.score;

        // If minimax finds a forced win, use it
        if (Math.abs(minimaxScore) > 50000) {
          return {
            fromPosition: minimaxMove.fromPosition,
            move: minimaxMove.move,
            score: minimaxScore,
            method: "tactical_minimax",
            confidence: 0.95,
            details: `Tactical minimax found ${minimaxScore > 0 ? "winning" : "defensive"} move`,
          };
        }
      }
    }

    // Phase 5: Run MCTS analysis
    let mctsMove: any = null;
    let mctsWinRate = 0.5;

    if (mctsTime > 500) {
      const remainingTime = maxTime - (Date.now() - startTime);
      const actualMCTSTime = Math.min(mctsTime, remainingTime * 0.95);

      mctsMove = this.mctsEngine.findBestMove(gameState, actualMCTSTime);

      if (mctsMove) {
        mctsWinRate = mctsMove.winRate;
      }
    }

    // Phase 6: Combine results
    const decision = this.combineResults(
      positionType,
      minimaxMove,
      minimaxScore,
      mctsMove,
      mctsWinRate,
      patternMove
    );

    return decision;
  }

  /**
   * Classify position type for time allocation
   */
  private classifyPosition(gameState: GameState): "tactical" | "complex" | "endgame" | "balanced" {
    let totalPieces = 0;
    let kings = 0;
    let hasCaptures = false;

    // Count pieces and check for captures
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        totalPieces += pieces.length;

        for (const piece of pieces) {
          if (piece.isKing) kings++;

          // Allow control if piece is the current player's color, or if it is zombified
          const isOwnColor = piece.isLight === gameState.isLightTurn;
          const isZombified = piece.zombifiedStatus?.isZombified || false;
          if ((isOwnColor || isZombified) && !piece.poisonedStatus?.isPoisoned) {
            const position: Position = { row, col };
            try {
              const moves = piece.getValidMovesForPiece(gameState.board, position, gameState.isLightTurn, undefined);
              for (const move of moves) {
                if (move.toPiece) hasCaptures = true;
              }
            } catch (error) {
              // Ignore
            }
          }
        }
      }
    }

    // Endgame: Few pieces remaining
    if (totalPieces <= 8) return "endgame";

    // Tactical: Captures available or kings near goal
    if (hasCaptures || this.hasKingNearGoal(gameState)) return "tactical";

    // Complex: Many pieces, no clear tactics
    if (totalPieces >= 16) return "complex";

    return "balanced";
  }

  /**
   * Check if any king is near goal
   */
  private hasKingNearGoal(gameState: GameState): boolean {
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (!piece.isKing) continue;

          const goalRow = piece.isLight ? 7 : 0;
          const distance = Math.abs(row - goalRow);

          if (distance <= 2) return true;
        }
      }
    }
    return false;
  }

  /**
   * Check for race emergency situations
   * Returns threat level based on king positions
   */
  private checkRaceEmergency(gameState: GameState): "critical" | "dangerous" | "safe" {
    let ourKingRow = -1;
    let enemyKingRow = -1;

    // Find both kings
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 5; col++) {
        const pieces = gameState.board[row][col].Pieces;
        for (const piece of pieces) {
          if (!piece.isKing) continue;

          if (piece.isLight === gameState.isLightTurn) {
            ourKingRow = row;
          } else {
            enemyKingRow = row;
          }
        }
      }
    }

    // Calculate distances to goal
    const ourGoalRow = gameState.isLightTurn ? 7 : 0;
    const enemyGoalRow = gameState.isLightTurn ? 0 : 7;

    const ourDistance = ourKingRow !== -1 ? Math.abs(ourKingRow - ourGoalRow) : 999;
    const enemyDistance = enemyKingRow !== -1 ? Math.abs(enemyKingRow - enemyGoalRow) : 999;

    // CRITICAL: Enemy is 1-2 moves from victory OR we're losing the race badly
    if (enemyDistance <= 1) {
      return "critical"; // IMMEDIATE THREAT!
    }

    if (enemyDistance <= 2 && enemyDistance < ourDistance) {
      return "critical"; // Enemy closer and very close
    }

    // DANGEROUS: Enemy is 3 moves away or we're in a close race
    if (enemyDistance <= 3) {
      return "dangerous";
    }

    if (ourDistance <= 3 && Math.abs(ourDistance - enemyDistance) <= 1) {
      return "dangerous"; // Tight race situation
    }

    return "safe";
  }

  /**
   * Evaluate pattern strength
   */
  private evaluatePatternStrength(gameState: GameState): number {
    const matches = this.patternLibrary.matchPatterns(gameState);
    if (matches.length === 0) return 0;

    // Higher priority patterns = higher strength
    const topPriority = matches[0].priority;
    return Math.min(0.95, 0.5 + topPriority * 0.05);
  }

  /**
   * Combine results from multiple methods
   */
  private combineResults(
    positionType: string,
    minimaxMove: any,
    minimaxScore: number,
    mctsMove: any,
    mctsWinRate: number,
    patternMove: any
  ): HybridResult | null {
    // If both agree, high confidence
    if (
      minimaxMove &&
      mctsMove &&
      this.movesMatch(minimaxMove, mctsMove.move)
    ) {
      return {
        fromPosition: minimaxMove.fromPosition,
        move: minimaxMove.move,
        score: minimaxScore,
        method: "hybrid",
        confidence: 0.95,
        details: "Both minimax and MCTS agree on this move",
      };
    }

    // Prefer minimax for tactical positions with clear advantage
    if (positionType === "tactical" && minimaxMove && Math.abs(minimaxScore) > 1000) {
      return {
        fromPosition: minimaxMove.fromPosition,
        move: minimaxMove.move,
        score: minimaxScore,
        method: "tactical_minimax",
        confidence: 0.85,
        details: "Tactical position with clear minimax evaluation",
      };
    }

    // Prefer MCTS for complex positions with strong consensus
    if (positionType === "complex" && mctsMove && mctsMove.visits > 1000) {
      return {
        fromPosition: mctsMove.move.fromPosition,
        move: mctsMove.move.move,
        score: mctsWinRate * 1000,
        method: "mcts",
        confidence: 0.85,
        details: "Complex position with strong MCTS consensus",
      };
    }

    // Default: Use method with stronger signal
    if (minimaxMove && mctsMove) {
      const minimaxConfidence = Math.min(0.9, Math.abs(minimaxScore) / 10000);
      const mctsConfidence = Math.abs(mctsWinRate - 0.5) * 2;

      if (minimaxConfidence > mctsConfidence) {
        return {
          fromPosition: minimaxMove.fromPosition,
          move: minimaxMove.move,
          score: minimaxScore,
          method: "tactical_minimax",
          confidence: minimaxConfidence,
          details: "Minimax has stronger evaluation",
        };
      } else {
        return {
          fromPosition: mctsMove.move.fromPosition,
          move: mctsMove.move.move,
          score: mctsWinRate * 1000,
          method: "mcts",
          confidence: mctsConfidence,
          details: "MCTS has stronger consensus",
        };
      }
    }

    // Fallback to whichever method succeeded
    if (minimaxMove) {
      return {
        fromPosition: minimaxMove.fromPosition,
        move: minimaxMove.move,
        score: minimaxScore,
        method: "tactical_minimax",
        confidence: 0.6,
        details: "Only minimax available",
      };
    }

    if (mctsMove) {
      return {
        fromPosition: mctsMove.move.fromPosition,
        move: mctsMove.move.move,
        score: mctsWinRate * 1000,
        method: "mcts",
        confidence: 0.6,
        details: "Only MCTS available",
      };
    }

    // Last resort: pattern move
    if (patternMove) {
      return {
        fromPosition: patternMove.fromPosition,
        move: patternMove.move,
        score: 0,
        method: "pattern",
        confidence: 0.5,
        details: "Fallback to pattern move",
      };
    }

    return null;
  }

  /**
   * Check if two moves are the same
   */
  private movesMatch(move1: any, move2: any): boolean {
    return (
      move1.fromPosition.row === move2.fromPosition.row &&
      move1.fromPosition.col === move2.fromPosition.col &&
      move1.move.position.row === move2.move.position.row &&
      move1.move.position.col === move2.move.position.col
    );
  }
}